// src/pages/home/EmploymentSection.jsx
import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import "./employmentsection.css";

// Categorías y Subcategorías
const JOB_CATEGORIES = {
  "Administración": ["Contabilidad", "Recursos Humanos", "Administración de Empresas"],
  "Tecnología": ["Desarrollo de Software", "Soporte Técnico", "Redes", "Ciberseguridad"],
  "Salud": ["Enfermería", "Medicina", "Terapia Física"],
  "Comercio": ["Ventas", "Marketing", "Atención al Cliente"],
  "Construcción": ["Arquitectura", "Ingeniería Civil", "Obras"],
  "Educación": ["Docencia", "Administración Educativa"],
  "Hostelería": ["Cocina", "Servicio al Cliente", "Gestión Hotelera"],
  "Transporte": ["Conducción", "Logística", "Mantenimiento"],
  "Servicios": ["Limpieza", "Mantenimiento", "Consultoría"]
};

const STATES_US = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawái", "Idaho",
  "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Luisiana",
  "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Misisipi",
  "Misuri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey",
  "New Mexico", "New York", "Carolina del Norte", "Dakota del Norte",
  "Ohio", "Oklahoma", "Oregón", "Pensilvania", "Carolina del Sur",
  "Dakota del Sur", "Tennessee", "Texas", "Utah", "Vermont", "Virginia",
  "Washington", "Virginia Occidental", "Wisconsin", "Wyoming"
];

function EmploymentSection() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // Filtros temporales
  const [tempState, setTempState] = useState("");
  const [tempCategory, setTempCategory] = useState("");
  const [tempSubcategory, setTempSubcategory] = useState("");

  // Lista de empleos y empleos filtrados
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [searched, setSearched] = useState(false); // Para saber si ya se hizo búsqueda

  useEffect(() => {
    const storedJobs = JSON.parse(localStorage.getItem("jobs")) || [];
    const publishedJobs = storedJobs.filter((job) => job.status === "Publicado");
    setJobs(publishedJobs);
    setFilteredJobs([]);
  }, []);

  const handleSearch = () => {
    if (!tempState || !tempCategory || !tempSubcategory) {
      alert("Por favor, selecciona Estado, Categoría y Subcategoría antes de buscar.");
      return;
    }
    setSearched(true);

    const results = jobs.filter((job) => {
      return (
        job.stateUS.toLowerCase() === tempState.toLowerCase() &&
        job.category === tempCategory &&
        job.subcategory === tempSubcategory
      );
    });
    setFilteredJobs(results);
  };

  // Mostramos máximo 10 resultados en esta página
  const MAX_RESULTS = 10;
  const displayedJobs = filteredJobs.slice(0, MAX_RESULTS);

  // Handler para agregar a favoritos
  const handleFavorite = (job) => {
    // Verificamos si el user es un "user" (no employer)
    if (!user || user.userType !== "user") {
      alert("Debes iniciar sesión como usuario para agregar favoritos.");
      navigate("/login?role=user");
      return;
    }
    // Guardamos en localStorage
    const storedFavs = JSON.parse(localStorage.getItem("favorites")) || [];
    const alreadyFav = storedFavs.find((f) => f.id === job.id);
    if (alreadyFav) {
      alert("Este empleo ya está en tus favoritos.");
      return;
    }
    storedFavs.push({
      id: job.id,
      category: "Empleo",
      resource: job.title,
      link: `/detalle-empleo/${job.id}`
    });
    localStorage.setItem("favorites", JSON.stringify(storedFavs));
    alert("¡Empleo guardado en tus favoritos!");
  };

  // Handler para aplicar
  const handleApply = (job) => {
    if (!user || user.userType !== "user") {
      alert("Debes iniciar sesión como usuario para aplicar a un empleo.");
      navigate("/login?role=user");
      return;
    }
    // Redirigimos al formulario de aplicación
    navigate(`/aplicar?jobId=${job.id}`);
  };

  return (
    <section className="job-search-section">
      <div className="job-search-container">
        <h1 className="job-search-title">Búsqueda de Empleo</h1>

        <div className="job-search-filters">
          <select
            className="filter-input"
            value={tempState}
            onChange={(e) => setTempState(e.target.value)}
          >
            <option value="">Seleccione un Estado *</option>
            {STATES_US.map((st) => (
              <option key={st} value={st}>{st}</option>
            ))}
          </select>

          <select
            className="filter-input"
            value={tempCategory}
            onChange={(e) => {
              setTempCategory(e.target.value);
              setTempSubcategory("");
            }}
          >
            <option value="">Seleccione una Categoría *</option>
            {Object.keys(JOB_CATEGORIES).map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <select
            className="filter-input"
            value={tempSubcategory}
            onChange={(e) => setTempSubcategory(e.target.value)}
            disabled={!tempCategory}
          >
            <option value="">Seleccione una Subcategoría *</option>
            {tempCategory &&
              JOB_CATEGORIES[tempCategory].map((subcat) => (
                <option key={subcat} value={subcat}>{subcat}</option>
              ))}
          </select>

          <button className="filter-button" onClick={handleSearch}>
            BUSCAR
          </button>
        </div>

        <div className="job-list">
          {searched && filteredJobs.length === 0 ? (
            <p className="no-results-message">
              No se encontraron empleos para los criterios seleccionados.
            </p>
          ) : (
            <>
              {displayedJobs.map((job) => {
                const snippet = job.description
                  ? job.description.slice(0, 80) + (job.description.length > 80 ? "..." : "")
                  : "";

                return (
                  <div key={job.id} className="job-card">
                    <h3 className="job-card-title">{job.title}</h3>
                    <p className="job-card-location">
                      {job.stateUS} - {job.category} / {job.subcategory}
                    </p>
                    <p className="job-card-salary">Salario: {job.salary}</p>
                    <p className="job-card-snippet">{snippet}</p>
                    <div className="job-card-actions">
                      <a
                        href={`/detalle-empleo/${job.id}`}
                        className="btn-vermas"
                        style={{ backgroundColor: "#007bff", color: "#fff" }}
                      >
                        Ver más
                      </a>
                      <button
                        className="btn-favorite"
                        style={{ backgroundColor: "#28a745", color: "#fff" }}
                        onClick={() => handleFavorite(job)}
                      >
                        Favorito
                      </button>
                      <button
                        className="btn-apply"
                        style={{ backgroundColor: "#fd7e14", color: "#fff" }}
                        onClick={() => handleApply(job)}
                      >
                        Aplicar
                      </button>
                    </div>
                  </div>
                );
              })}
              {filteredJobs.length > MAX_RESULTS && (
                <button
                  className="pagination-button"
                  onClick={() => navigate("/empleos-disponibles")}
                >
                  Ver más resultados
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export default EmploymentSection;